import * as React from 'react';
import { FunctionComponent, useState } from 'react';

import RegExpEscape from 'regexp.escape';
import { StyleObject } from 'styletron-standard';

import { MoveDownIcon } from 'shared/components/ihcl/icon';
import { styled, useStyletron } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import { Theme } from 'shared/components/ihcl/theme-provider';

import { Tag } from './tag';

const DefaultSummaryWrapper = styled('div', {});

type DetailsProps = {
  children: React.ReactNode;
  closedSummary: React.ReactNode;
  openSummary: React.ReactNode;
  SummaryWrapper?: React.ComponentType;
};
export const Details: React.FC<DetailsProps> = ({
  children,
  closedSummary,
  openSummary,
  SummaryWrapper = DefaultSummaryWrapper,
}) => {
  const [css] = useStyletron();
  const [isOpen, setIsOpen] = useState(false);
  return (
    <details onToggle={() => setIsOpen(!isOpen)}>
      <summary className={css({ cursor: 'pointer' })}>
        <SummaryWrapper>{isOpen ? openSummary : closedSummary}</SummaryWrapper>
      </summary>
      {children}
    </details>
  );
};

export const StickySectionHeader = styled<'div', { $top: string }>(
  'div',
  ({ $top, $theme }) => ({
    position: 'sticky',
    flex: '1 0 100%',
    top: $top,
    zIndex: 1,
    borderTop: `1px solid ${$theme.colors.gray00}`,
    backgroundColor: $theme.colors.grayBackground,
    width: 'calc(100% + 16px)',
    marginLeft: `-${$theme.sizing.unit8}`,
    paddingTop: $theme.sizing.unit16,
    paddingRight: $theme.sizing.unit16,
    paddingBottom: $theme.sizing.unit16,
    paddingLeft: $theme.sizing.unit16,
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.06)',
  })
);

/*
// This is an attempt at using the $isStuck prop to conditionally apply the shadow
export const StickySectionHeader = styled(
  'div',
  ({ $isStuck, $top, $theme }: any) => ({
    position: 'sticky',
    top: '-1px',
    zIndex: 1,
    background:
      'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(249,249,251,1) 55%)',
    width: '100%',
    marginLeft: `-${$theme.sizing.unit8}`,
    marginRight: `-${$theme.sizing.unit8}`,
    marginTop: `-${$top}`,
    paddingTop: cssValueMath($theme.sizing.unit16, '+', $top),
    paddingRight: $theme.sizing.unit16,
    paddingBottom: $theme.sizing.unit16,
    paddingLeft: $theme.sizing.unit16,
    boxShadow: $isStuck ? '0px 4px 4px 0px rgba(0, 0, 0, 0.06)' : null,
  })
);
 */

export const SectionDivider = styled(
  'hr',
  ({ $hidden, $noBottomMargin, $theme }: any) => ({
    height: '8px',
    marginTop: '24px',
    marginBottom: $noBottomMargin ? 0 : '24px',
    backgroundColor: $theme.colors.gray00,
    display: $hidden ? 'none' : 'block',
  })
);

const StickySectionSubtitleWrapper = styled('div', ({ $theme }) => ({
  paddingLeft: $theme.sizing.unit16,
  paddingRight: $theme.sizing.unit24,
}));

export const CenteredText = styled('div', {
  textAlign: 'center',
});

export const FullWidthTextWrapper = styled('div', {
  flex: '0 0 100%',
});
export const StickySectionDivider: FunctionComponent<{
  title: React.ReactNode;
  subtitle: React.ReactNode;
  top?: string;
}> = ({ title, subtitle, top = '0' }) => (
  <>
    <StickySectionHeader $top={top}>
      <Text variant="LabelSmall">
        <MoveDownIcon height="16px" width="16px" />
        <strong>{title}</strong>
      </Text>
    </StickySectionHeader>
    <FullWidthTextWrapper>
      <StickySectionSubtitleWrapper>
        <Text variant="ParagraphXSmall" color="bodyCopyGray">
          {subtitle}
        </Text>
      </StickySectionSubtitleWrapper>
    </FullWidthTextWrapper>
  </>
);

const TagsWrapper = styled('div', {
  marginLeft: '-5px',
  marginRight: '5px',
});

const TagsLink = styled<'a', any>('a', ({ $theme }: any) => ({
  color: `${$theme.colors.grayLabel} !important`,
  display: 'block',
  marginTop: '5px',
  marginBottom: '56px',
  textDecoration: 'underline',
}));

type TagsProps = {
  tags: Array<String>;
  [key: string]: any;
};

export const Tags: React.FC<TagsProps> = ({ tags, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasMoreTags = tags.length > 1;
  const renderedTags = isOpen ? tags : tags.slice(0, 1);

  return (
    <>
      <TagsWrapper>
        {renderedTags.map((tag: String) => (
          // @ts-ignore
          <Tag {...props} key={tag}>
            {tag}
          </Tag>
        ))}
      </TagsWrapper>
      {hasMoreTags && (
        <TagsLink onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? 'Hide' : 'View'} details
        </TagsLink>
      )}
    </>
  );
};

type FullscreenContainerProps = {
  $backgroundColor?: string;
  $zIndex?: number;
  $clipPath?: string;
};

export const FullscreenContainer = styled<'div', FullscreenContainerProps>(
  'div',
  ({ $theme, $backgroundColor = null, $zIndex = null, $clipPath = null }) => ({
    position: 'fixed',
    left: 0,
    top: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: $backgroundColor || $theme.colors.grayBackground,
    zIndex: $zIndex || null,
    clipPath: $clipPath || null,
    overscrollBehavior: 'none',
    touchAction: 'none',
  })
);

export type StackProps = {
  $alignItems?: 'baseline' | 'center' | 'flex-start' | 'flex-end' | 'stretch';
  $alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch';
  $gapSizing?: keyof Theme['sizing'];
  $flexDirection?: 'column' | 'row';
  $flexWrap?: 'nowrap' | 'wrap';
  $justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around';
  ref?: any;
  style?: StyleObject;
};

export const Stack = styled<'div', StackProps>(
  'div',
  ({
    $theme,
    $alignItems = 'stretch',
    $alignSelf = 'auto',
    $flexDirection = 'column',
    $flexWrap = 'nowrap',
    $gapSizing,
    $justifyContent = 'flex-start',
    style = {},
  }: any) => ({
    alignItems: $alignItems,
    alignSelf: $alignSelf,
    display: 'flex',
    flexDirection: $flexDirection,
    flexWrap: $flexWrap,
    gap: $theme.sizing[$gapSizing],
    justifyContent: $justifyContent,
    ...style,
  })
);

type TextButtonProps = {
  $color?: string;
  $underline?: boolean;
  $size?: string;
  $bold?: boolean;
  $disabled?: boolean;
};

export const TextButton = styled<'button', TextButtonProps>(
  'button',
  ({
    $theme,
    $color = 'default',
    $size = 'default',
    $underline = false,
    $disabled,
    $bold = false,
  }: any) => {
    const color =
      $color === 'neutral' ? $theme.colors.grayLabel : $theme.colors.primary;
    const textDecoration = $color === 'neutral' ? 'underline' : 'none';
    const fontSize = $size === 'tiny' ? $theme.sizing.scale500 : null;
    const lineHeight = $size === 'tiny' ? $theme.sizing.scale600 : null;
    const paddingVertical = $size === 'tiny' ? $theme.sizing.scale100 : 0;
    const paddingHorizontal = $size === 'tiny' ? $theme.sizing.scale300 : 0;
    const fontWeight = $bold ? '800' : null;

    const styles = {
      border: 'none',
      color,
      cursor: 'pointer',
      textDecoration: $underline ? 'underline' : textDecoration,
      fontSize,
      fontWeight,
      lineHeight,
      backgroundColor: 'transparent',
      verticalAlign: 'top',
      paddingTop: paddingVertical,
      paddingBottom: paddingVertical,
      paddingRight: paddingHorizontal,
      paddingLeft: paddingHorizontal,
    };
    if ($disabled) {
      return {
        ...styles,
        color: $theme.colors.grayLabel,
        textDecoration: 'none',
        cursor: 'not-allowed',
      };
    }
    return styles;
  }
);

type MatchingTextHighlightProps = {
  searchTerm: string;
  text: string;
};

const HighlightedText = styled('span', () => ({
  fontWeight: 800,
}));

export const MatchingTextHighlight: React.FC<MatchingTextHighlightProps> = ({
  searchTerm = '',
  text = '',
}) => {
  if (searchTerm === '') {
    return <>{text}</>;
  }

  const parts = text.split(new RegExp(`(${RegExpEscape(searchTerm)})`, 'gi'));

  const keyedParts = parts
    .filter((part) => part !== '')
    .reduce<{ part: string; key: string; textSoFar: string }[]>((acc, part) => {
      const textSoFar = (acc.at(-1)?.textSoFar ?? '') + part;
      return [
        ...acc,
        { part, textSoFar, key: `htxt-${text}-${part}-${textSoFar}` },
      ];
    }, []);

  return (
    <span>
      {keyedParts.map(({ part, key }) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <HighlightedText key={key}>{part}</HighlightedText>
        ) : (
          part
        )
      )}
    </span>
  );
};

export const Window = styled('div', ({ $theme }) => ({
  paddingTop: $theme.sizing.unit16,
  paddingRight: $theme.sizing.unit20,
  paddingLeft: $theme.sizing.unit20,
  paddingBottom: $theme.sizing.unit24,
  borderRadius: $theme.sizing.unit16,
  borderStyle: 'solid',
  borderColor: $theme.colors.accent60,
  borderWidth: '1px',
  boxShadow: '0px 4px 16px 0px #591B7133',
  display: 'flex',
  flexDirection: 'column',
  gap: $theme.sizing.unit12,
}));
